'use client';

import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { usePathname, useSearchParams } from 'next/navigation';

import { useAuthContext } from '@/components/Providers/AuthProvider';
import { EnableSecurityCode, EnterCode } from '@/components/V2/Auth';
import Modal from '@/components/V2/Modal';
import AuthToast from '@/components/V2/Popup/AuthToast';
import { AUTH_MODAL_TOAST_CONTAINER } from '@/layouts/ToastWrapper/containerIds';
import { useSkip2FaMutation } from '@/store/api/twoFactorAuthentication';

export default function TwoFactorModalFlow() {
  const [showEnableSecurityModal, setShowEnableSecurityModal] = useState(false);
  const [showEnterCodeModal, setShowEnterCodeModal] = useState(false);
  const [codeData, setCodeData] = useState<{ smsSendTo: string; emailSendTo: string; expiry: string } | undefined>();
  // Use ref instead of state to prevent race conditions with React's async state updates
  const isCompletingFlowRef = useRef(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { customer } = useAuthContext();
  const [skip2Fa, { isLoading: isSkipping }] = useSkip2FaMutation();

  const isCustomerDataReady = !!(customer?.email && customer.email.trim());

  const removeShow2FAParam = () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('show2FA') === 'true') {
      urlParams.delete('show2FA');
      const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  };

  const removeEnter2FAParam = () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('enter2FA') === 'true') {
      urlParams.delete('enter2FA');
      const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  };

  const removeModalParam = () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('modal')) {
      urlParams.delete('modal');
      const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  };

  useEffect(() => {
    // Don't reopen modals if we're in the process of completing the flow
    // Check both ref and localStorage flag for synchronous detection across component boundaries
    if (isCompletingFlowRef.current) return;

    // Check localStorage flag set by EnterCode before RTK Query invalidation
    if (typeof window !== 'undefined' && localStorage.getItem('__2fa_flow_completing') === 'true') {
      isCompletingFlowRef.current = true;
      return;
    }

    try {
      // First, close all modals
      setShowEnableSecurityModal(false);
      setShowEnterCodeModal(false);

      // Then, check for new modal parameter first
      const modal = searchParams?.get('modal');
      if (modal === 'enter2FA') {
        setShowEnterCodeModal(true);
      } else if (modal === 'enable2FA') {
        setShowEnableSecurityModal(true);
      } else {
        // Fallback to old parameters for backward compatibility
        const shouldShow2FA = (searchParams?.get('show2FA') || '') === 'true';
        const shouldEnter2FA = (searchParams?.get('enter2FA') || '') === 'true';
        if (shouldEnter2FA) {
          setShowEnterCodeModal(true);
        } else if (shouldShow2FA) {
          setShowEnableSecurityModal(true);
        }
      }
    } catch (e) {}
  }, [searchParams, pathname]);

  const handleModalComplete = () => {
    setShowEnableSecurityModal(false);
    setShowEnterCodeModal(false);
    removeShow2FAParam();
    removeEnter2FAParam();
    removeModalParam();
  };

  const handleEnableSecuritySendCode = (codeData: { smsSendTo: string; emailSendTo: string; expiry: string }) => {
    setCodeData(codeData);
    setShowEnableSecurityModal(false);
    setShowEnterCodeModal(true);

    // Update URL to show enter2FA modal
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('modal', 'enter2FA');
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.replaceState({}, '', newUrl);
  };

  const handleEnableSecuritySkip = async () => {
    if (!customer?.customerID) {
      toast(
        ({ closeToast }) => (
          <AuthToast type="error" message="Customer information not available" closeToast={closeToast} />
        ),
        { containerId: AUTH_MODAL_TOAST_CONTAINER }
      );
      return;
    }

    try {
      const response = await skip2Fa({ customerId: customer.customerID }).unwrap();

      if (response.success) {
        toast(
          ({ closeToast }) => <AuthToast type="success" message="2FA skipped successfully" closeToast={closeToast} />,
          { containerId: AUTH_MODAL_TOAST_CONTAINER }
        );
      } else {
        toast(
          ({ closeToast }) => (
            <AuthToast type="error" message={response.message || 'Failed to skip 2FA'} closeToast={closeToast} />
          ),
          { containerId: AUTH_MODAL_TOAST_CONTAINER }
        );
      }
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.data?.exceptionMessage || 'Failed to skip 2FA';
      toast(({ closeToast }) => <AuthToast type="error" message={errorMessage} closeToast={closeToast} />, {
        containerId: AUTH_MODAL_TOAST_CONTAINER,
      });
    }

    setShowEnableSecurityModal(false);
    removeShow2FAParam();
    removeModalParam();
    handleModalComplete();
  };

  const handleEnterCodeVerify = () => {
    // Set ref immediately to prevent useEffect from reopening the modal
    // Using ref ensures synchronous update that's immune to React's batching
    isCompletingFlowRef.current = true;
    setShowEnterCodeModal(false);

    // Clean up the localStorage flag (set by EnterCode component)
    if (typeof window !== 'undefined') {
      localStorage.removeItem('__2fa_flow_completing');
    }

    // Handle redirect for enter2FA flow
    try {
      const redirectUrl = typeof window !== 'undefined' ? localStorage.getItem('__2fa_redirect_url') : null;
      if (redirectUrl) {
        localStorage.removeItem('__2fa_redirect_url');

        const urlParams = new URLSearchParams(window.location.search);
        urlParams.delete('modal');
        const cleanUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
        window.history.replaceState({}, '', cleanUrl);

        setTimeout(() => {
          window.location.replace(redirectUrl);
        }, 100);
        return;
      }
    } catch (e) {
      console.error('Failed to handle redirect:', e);
    }

    // Clean up and complete
    handleModalComplete();
  };

  const handleEnterCodeBack = () => {
    setShowEnterCodeModal(false);
    setShowEnableSecurityModal(true);

    // Update URL to show enable2FA modal
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('modal', 'enable2FA');
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.replaceState({}, '', newUrl);
  };

  return (
    <>
      <Modal
        isOpen={showEnableSecurityModal}
        onClose={() => setShowEnableSecurityModal(false)}
        className="v2-w-[640px] v2-py-6 md:v2-py-8 v2-px-4 md:v2-px-8"
        closeButton={() => null}
      >
        <EnableSecurityCode
          onSendCode={handleEnableSecuritySendCode}
          onSkip={handleEnableSecuritySkip}
          onBack={() => setShowEnableSecurityModal(false)}
          isSkipLoading={isSkipping}
          isCustomerLoading={!isCustomerDataReady}
        />
      </Modal>
      <Modal
        isOpen={showEnterCodeModal}
        onClose={() => setShowEnterCodeModal(false)}
        className="md:!v2-w-[640px] v2-py-4 md:v2-py-8 v2-px-4 md:v2-px-8"
        closeButton={() => null}
      >
        <EnterCode
          onVerify={handleEnterCodeVerify}
          onBack={handleEnterCodeBack}
          onResend={() => console.log('Resend code')}
          codeData={codeData}
        />
      </Modal>
    </>
  );
}
